// Live's rules for a voice made from a recording or a file, and its words (`constants/
// upload-media-files.tsx`, `services/voices/voice-upload/validation/schemas.ts`, `constants/
// audio.ts`, `voice-create/**`). The server holds the same limits (G7-team-voices).
import type { VoiceMime } from "../../api/generated/model";

/** Live's length range, in seconds, and its size cap. */
export const VOICE_MIN_SECONDS = 15;
export const VOICE_MAX_SECONDS = 60;
export const VOICE_MAX_BYTES = 20 * 1024 * 1024;

/** A voice's name: 1 to 99 characters once trimmed (live's `MAX_NAME_LENGTH` of 100, exclusive). */
export const VOICE_NAME_MAX = 99;

/**
 * The types taken, by the type the browser gives the file alone: live's (`VOICE_ALLOWED_TYPES`)
 * and `audio/mp4`, as Chrome and Safari label most .m4a files, which live refuses by mistake
 * (SA3 difference 5).
 */
const VOICE_TYPES: readonly VoiceMime[] = [
  "audio/mpeg",
  "audio/mp3",
  "audio/mp4",
  "audio/x-m4a",
  "audio/wav",
  "audio/wave",
  "audio/x-wav",
  "audio/x-pn-wav",
];

/** Live's file input `accept`: its types, then its three extensions. */
export const VOICE_ACCEPT = [...VOICE_TYPES, ".mp3", ".m4a", ".wav"].join(",");

/** Live's words under the drop zone. */
export const UPLOAD_HINT =
  "Upload one audio file in MP3, M4A, or WAV format, lasting 15 seconds to 1 minute and under 20MB.";

/** Live's validation words, in its order. */
export const VOICE_PROBLEMS = {
  format: "Voice unsupported format. Please upload MP3, M4A, or WAV.",
  size: "Voice too large (max 20 MB).",
  short: "Voice is too short (min 15 seconds).",
  long: "Voice is too long (max 1 minutes).",
  unread: "Could not read audio duration.",
} as const;

/** The type the server stores a file under, or null where live takes none (a typeless file too). */
export function voiceMimeOf(file: Blob): VoiceMime | null {
  return VOICE_TYPES.find((type) => type === file.type) ?? null;
}

/**
 * What is wrong with a file as a voice, in live's words and order: a type it does not take, too
 * large, then its length (`seconds`, null when it could not be read), which a wrong type skips.
 */
export function voiceProblems(file: Blob, seconds: number | null): string[] {
  const problems: string[] = [];
  const format = voiceMimeOf(file) === null;
  if (format) problems.push(VOICE_PROBLEMS.format);
  if (file.size > VOICE_MAX_BYTES) problems.push(VOICE_PROBLEMS.size);
  if (format) return problems;
  if (seconds === null) problems.push(VOICE_PROBLEMS.unread);
  else if (seconds < VOICE_MIN_SECONDS) problems.push(VOICE_PROBLEMS.short);
  else if (seconds > VOICE_MAX_SECONDS) problems.push(VOICE_PROBLEMS.long);
  return problems;
}

/** Live's rule for Save: a name of 1 to 99 characters once trimmed. */
export function nameFits(name: string): boolean {
  const trimmed = name.trim();
  return trimmed.length > 0 && trimmed.length <= VOICE_NAME_MAX;
}
